import { useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FolderPlus,
  RefreshCw,
  Star,
  StarOff,
} from "lucide-react";
import { MailCreateFolderDialog } from "@/features/folders/MailCreateFolderDialog";
import { flattenFoldersForPicker } from "@/lib/folders/folderTree";
import {
  NAV_FOLDER_LABELS,
  NAV_FOLDER_ORDER,
  buildNavLayoutFromSettings,
  defaultPrimaryNavOrder,
  navFolderSlot,
  pickBestFolderForSlot,
  type NavFolderSlot,
} from "@/lib/mail/folderNavSections";
import type { MailAccountSettings, PrimaryNavEntry } from "@/lib/mail/accountSettingsPrefs";
import {
  folderSlotStatusSummaryLabel,
  resolveFolderSlotImapStatus,
  type FolderSlotImapStatusKind,
} from "@/lib/mail/folderSlotImapStatus";
import {
  decodeAureliaImapName,
  findAureliaFolderForSlot,
  formatImapFolderPickerLabel,
} from "@/lib/mail/aureliaImapFolders";
import { sentFolderSetupHint } from "@/lib/mail/sentFolderSetup";
import type { MailFolder } from "@/lib/types/mail";
import { cn } from "@/lib/cn";

type Props = {
  accountId: number;
  folders: MailFolder[];
  draft: MailAccountSettings;
  onChange: (patch: Partial<MailAccountSettings>) => void;
  onFolderCreated?: (folder: MailFolder) => void;
  onRefreshFolders?: () => void;
  foldersRefreshing?: boolean;
};

function entryKey(entry: PrimaryNavEntry): string {
  return entry.type === "slot" ? `slot:${entry.slot}` : `folder:${entry.name}`;
}

function moveEntry(order: PrimaryNavEntry[], index: number, delta: number): PrimaryNavEntry[] {
  const next = index + delta;
  if (next < 0 || next >= order.length) return order;
  const copy = [...order];
  const [item] = copy.splice(index, 1);
  copy.splice(next, 0, item);
  return copy;
}

function SlotImapBadge({ kind, label }: { kind: FolderSlotImapStatusKind; label: string }) {
  if (kind === "local") {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[var(--mail-surface-hover)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--mail-muted)]">
        {label}
      </span>
    );
  }
  if (kind === "ok") {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
        <CheckCircle2 className="h-3 w-3" strokeWidth={2.25} aria-hidden />
        {label}
      </span>
    );
  }
  if (kind === "ambiguous") {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
        <AlertCircle className="h-3 w-3" aria-hidden />
        {label}
      </span>
    );
  }
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700">
      <AlertCircle className="h-3 w-3" aria-hidden />
      {label}
    </span>
  );
}

export function MailFolderLayoutEditor({
  accountId,
  folders,
  draft,
  onChange,
  onFolderCreated,
  onRefreshFolders,
  foldersRefreshing = false,
}: Props) {
  const [createForSlot, setCreateForSlot] = useState<NavFolderSlot | null>(null);
  const flat = flattenFoldersForPicker(folders);
  const navOrder =
    draft.primaryNavOrder.length > 0 ? draft.primaryNavOrder : defaultPrimaryNavOrder();
  const demoted = new Set(draft.demotedNavSlots);
  const hidden = new Set(draft.hiddenNavSlots);

  const preview = buildNavLayoutFromSettings(folders, draft);
  const primaryNames = new Set(preview.primary.map((f) => f.name));

  const statusSummary = useMemo(
    () => folderSlotStatusSummaryLabel(folders, draft.folderSlotOverrides),
    [draft.folderSlotOverrides, folders]
  );

  function patchOrder(nextOrder: PrimaryNavEntry[]) {
    onChange({ primaryNavOrder: nextOrder });
  }

  function setSlotOverride(slot: NavFolderSlot, folderName: string) {
    const overrides = { ...draft.folderSlotOverrides };
    if (!folderName) delete overrides[slot];
    else overrides[slot] = folderName;
    onChange({ folderSlotOverrides: overrides });
  }

  function demoteSlot(slot: NavFolderSlot) {
    onChange({ demotedNavSlots: [...new Set([...draft.demotedNavSlots, slot])] });
  }

  function promoteFolder(name: string) {
    if (primaryNames.has(name)) return;
    onChange({ primaryNavOrder: [...navOrder, { type: "folder", name }] });
  }

  function demoteFolder(name: string) {
    onChange({
      primaryNavOrder: navOrder.filter((e) => !(e.type === "folder" && e.name === name)),
    });
  }

  function promoteSlot(slot: NavFolderSlot) {
    onChange({ demotedNavSlots: draft.demotedNavSlots.filter((s) => s !== slot) });
  }

  const autoSlotFolder = (slot: NavFolderSlot) => {
    const aurelia = findAureliaFolderForSlot(flat, slot);
    if (aurelia) return aurelia;
    return pickBestFolderForSlot(
      slot,
      flat.filter((folder) => navFolderSlot(folder.name, folder.label) === slot)
    );
  };

  function folderPickerLabel(folder: MailFolder, slot: NavFolderSlot): string {
    const base = formatImapFolderPickerLabel(folder);
    const detected = navFolderSlot(folder.name, folder.label);
    if (detected !== slot) return base;
    const best = autoSlotFolder(slot);
    if (best?.name === folder.name) {
      return `${base} (recommandé)`;
    }
    return `${base} (${NAV_FOLDER_LABELS[slot]})`;
  }

  const sentAmbiguityHint = sentFolderSetupHint(folders);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 space-y-1">
          <p className="text-xs text-[var(--mail-muted)]">
            Réorganisez l’affichage de la barre latérale. Créez des dossiers IMAP ou associez un dossier
            existant à chaque rôle (En attente, Brouillons…).
          </p>
          <p
            className={cn(
              "text-[11px] font-medium",
              statusSummary.startsWith("Tous")
                ? "text-emerald-700"
                : "text-amber-800"
            )}
          >
            {statusSummary}
          </p>
        </div>
        {onRefreshFolders ? (
          <button
            type="button"
            onClick={onRefreshFolders}
            disabled={foldersRefreshing}
            className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-[var(--mail-border-soft)] px-2.5 py-1.5 text-[10px] font-medium text-[var(--mail-text)] transition hover:bg-[var(--mail-surface-hover)] disabled:opacity-50"
          >
            <RefreshCw
              className={cn("h-3 w-3", foldersRefreshing && "animate-spin")}
              aria-hidden
            />
            Rafraîchir IMAP
          </button>
        ) : null}
      </div>

      <div>
        <p className="mb-2 text-[11px] font-semibold text-[var(--mail-text)]">Barre principale</p>
        <ul className="space-y-1.5">
          {navOrder.map((entry, index) => {
            if (entry.type === "slot") {
              const slot = entry.slot;
              const isDemoted = demoted.has(slot);
              const isHidden = hidden.has(slot);
              const auto = autoSlotFolder(slot);
              const override = draft.folderSlotOverrides[slot] ?? "";
              const imapStatus = resolveFolderSlotImapStatus(
                slot,
                folders,
                draft.folderSlotOverrides
              );

              return (
                <li
                  key={entryKey(entry)}
                  className={cn(
                    "rounded-none border border-[var(--mail-border-soft)] bg-[var(--mail-bg)]/40 px-3 py-2",
                    isDemoted || isHidden ? "opacity-55" : ""
                  )}
                >
                  <div className="flex items-center gap-1">
                    <span className="min-w-0 flex-1 text-sm font-medium">{NAV_FOLDER_LABELS[slot]}</span>
                    <SlotImapBadge kind={imapStatus.kind} label={imapStatus.statusLabel} />
                    <button
                      type="button"
                      disabled={index === 0}
                      onClick={() => patchOrder(moveEntry(navOrder, index, -1))}
                      className="rounded p-1 text-[var(--mail-muted)] hover:bg-[var(--mail-surface-hover)] disabled:opacity-30"
                      aria-label="Monter"
                    >
                      <ChevronUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      disabled={index >= navOrder.length - 1}
                      onClick={() => patchOrder(moveEntry(navOrder, index, 1))}
                      className="rounded p-1 text-[var(--mail-muted)] hover:bg-[var(--mail-surface-hover)] disabled:opacity-30"
                      aria-label="Descendre"
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                    {!isDemoted ? (
                      <button
                        type="button"
                        onClick={() => demoteSlot(slot)}
                        className="rounded p-1 text-[var(--mail-muted)] hover:bg-[var(--mail-surface-hover)]"
                        title="Déplacer dans Tous les dossiers"
                        aria-label="Déplacer dans Tous les dossiers"
                      >
                        <StarOff className="h-3.5 w-3.5" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => promoteSlot(slot)}
                        className="rounded p-1 text-[var(--mail-accent)] hover:bg-[var(--mail-accent-soft)]"
                        title="Remonter dans la barre principale"
                        aria-label="Remonter"
                      >
                        <Star className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <label className="mt-2 block text-[10px] text-[var(--mail-muted)]">
                    {slot === "outbox" ? (
                      <span className="mt-1 block text-[var(--mail-muted)]">
                        File locale Aurélia — pas de dossier IMAP.
                      </span>
                    ) : (
                      <>
                        Dossier IMAP
                        <select
                          value={override}
                          onChange={(e) => setSlotOverride(slot, e.target.value)}
                          className="mt-1 w-full rounded-lg border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] px-2 py-1.5 text-xs"
                        >
                          <option value="">
                            {auto
                              ? `Auto : ${formatImapFolderPickerLabel(auto)}`
                              : "Aucun détecté"}
                          </option>
                          {flat.map((f) => (
                            <option key={f.name} value={f.name}>
                              {folderPickerLabel(f, slot)}
                            </option>
                          ))}
                        </select>
                      </>
                    )}
                  </label>
                  {slot === "sent" && sentAmbiguityHint ? (
                    <p className="mt-2 rounded-lg bg-amber-50 px-2 py-1.5 text-[10px] text-amber-900">
                      {sentAmbiguityHint}
                    </p>
                  ) : null}
                  {slot !== "outbox" && imapStatus.imapPath ? (
                    <p className="mt-1 truncate text-[10px] text-[var(--mail-muted)]">
                      IMAP : {decodeAureliaImapName(imapStatus.imapPath)}
                    </p>
                  ) : null}
                  {slot !== "outbox" && imapStatus.kind === "missing" ? (
                    <p className="mt-1 text-[10px] text-red-700">
                      Aucun dossier IMAP associé — créez-en un ou choisissez-en un ci-dessus.
                    </p>
                  ) : null}
                  {slot !== "outbox" ? (
                    <button
                      type="button"
                      onClick={() => setCreateForSlot(slot)}
                      className="mt-2 inline-flex items-center gap-1 text-[10px] font-medium text-[var(--mail-accent)] hover:underline"
                    >
                      <FolderPlus className="h-3 w-3" aria-hidden />
                      Créer un dossier…
                    </button>
                  ) : null}
                  {isDemoted ? (
                    <p className="mt-1 text-[10px] text-amber-600">Dans « Tous les dossiers »</p>
                  ) : null}
                </li>
              );
            }

            const folder = flat.find((f) => f.name === entry.name);
            if (!folder) return null;

            return (
              <li
                key={entryKey(entry)}
                className="rounded-none border border-[var(--mail-border-soft)] bg-[var(--mail-bg)]/40 px-3 py-2"
              >
                <div className="flex items-center gap-1">
                  <span className="min-w-0 flex-1 truncate text-sm">{folder.label}</span>
                  <button
                    type="button"
                    disabled={index === 0}
                    onClick={() => patchOrder(moveEntry(navOrder, index, -1))}
                    className="rounded p-1 text-[var(--mail-muted)] hover:bg-[var(--mail-surface-hover)] disabled:opacity-30"
                    aria-label="Monter"
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    disabled={index >= navOrder.length - 1}
                    onClick={() => patchOrder(moveEntry(navOrder, index, 1))}
                    className="rounded p-1 text-[var(--mail-muted)] hover:bg-[var(--mail-surface-hover)] disabled:opacity-30"
                    aria-label="Descendre"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => demoteFolder(folder.name)}
                    className="rounded p-1 text-[var(--mail-muted)] hover:bg-[var(--mail-surface-hover)]"
                    title="Retirer de la barre principale"
                    aria-label="Retirer"
                  >
                    <StarOff className="h-3.5 w-3.5" />
                  </button>
                </div>
                <p className="mt-1 truncate text-[10px] text-[var(--mail-muted)]">{folder.name}</p>
              </li>
            );
          })}
        </ul>
      </div>

      {draft.showExtraFolders ? (
        <div>
          <p className="mb-2 text-[11px] font-semibold text-[var(--mail-text)]">
            Tous les dossiers ({preview.others.length})
          </p>
          {preview.others.length === 0 ? (
            <p className="text-xs text-[var(--mail-muted)]">Aucun dossier supplémentaire.</p>
          ) : (
            <ul className="max-h-40 space-y-1 overflow-y-auto">
              {preview.others.map((folder) => {
                const slot = navFolderSlot(folder.name, folder.label);
                return (
                  <li
                    key={folder.name}
                    className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-[var(--mail-surface-hover)]"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs">{formatImapFolderPickerLabel(folder)}</p>
                      {slot ? (
                        <p className="text-[10px] text-[var(--mail-muted)]">
                          Détecté : {NAV_FOLDER_LABELS[slot]}
                        </p>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      onClick={() => promoteFolder(folder.name)}
                      className="shrink-0 rounded-full px-2 py-1 text-[10px] font-medium text-[var(--mail-accent)] hover:bg-[var(--mail-accent-soft)]"
                    >
                      Remonter
                    </button>
                    {slot ? (
                      <button
                        type="button"
                        onClick={() => setSlotOverride(slot, folder.name)}
                        className="shrink-0 rounded-full px-2 py-1 text-[10px] text-[var(--mail-muted)] hover:bg-[var(--mail-surface-hover)]"
                        title={`Utiliser comme ${NAV_FOLDER_LABELS[slot]}`}
                      >
                        = {NAV_FOLDER_LABELS[slot]}
                      </button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}

      <details className="rounded-none border border-[var(--mail-border-soft)] px-3 py-2 text-xs">
        <summary className="cursor-pointer font-medium text-[var(--mail-text)]">
          Visibilité des raccourcis
        </summary>
        <ul className="mt-2 space-y-1">
          {NAV_FOLDER_ORDER.map((slot) => {
            const checked = !draft.hiddenNavSlots.includes(slot);
            return (
              <li key={slot}>
                <label className="flex cursor-pointer items-center gap-2 rounded-lg px-1 py-1.5 hover:bg-[var(--mail-surface-hover)]">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      const next = new Set(draft.hiddenNavSlots);
                      if (checked) next.add(slot);
                      else next.delete(slot);
                      onChange({ hiddenNavSlots: [...next] });
                    }}
                    className="h-4 w-4 accent-[var(--mail-accent)]"
                  />
                  <span>{NAV_FOLDER_LABELS[slot]}</span>
                </label>
              </li>
            );
          })}
        </ul>
      </details>

      <MailCreateFolderDialog
        open={createForSlot != null}
        accountId={accountId}
        folders={folders}
        defaultName={createForSlot === "snoozed" ? "Attente" : ""}
        title={
          createForSlot === "snoozed"
            ? "Créer le dossier En attente"
            : "Nouveau dossier IMAP"
        }
        onClose={() => setCreateForSlot(null)}
        onCreated={(folder) => {
          if (createForSlot) {
            setSlotOverride(createForSlot, folder.name);
          }
          onFolderCreated?.(folder);
          setCreateForSlot(null);
        }}
      />
    </div>
  );
}
